import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import { getCategoryLabel } from "@/lib/autocompleteCategoryLabel"
import { absoluteHref } from "@/lib/embed/absoluteHref"
import { buildPropertyHref } from "@/lib/embed/buildPropertyHref"
import type { Category, CategoryResult } from "@/types/search"
import {
  DEBOUNCE_MS,
  debounce,
  fetchPropertyAutocomplete,
  MIN_QUERY,
} from "../lib/fetchAutocomplete"
import {
  loadSuggestions,
  recordRecentSearch,
  type Suggestion,
} from "../lib/suggestions"
import { useFocusTrap } from "../hooks/useFocusTrap"
import type { EmbedUi, PropertyTab } from "../types"
import { overrideSearchParamsForTab } from "../config"
import { AutocompleteShell } from "./AutocompleteShell"
import { ResultsList, type ResultItem } from "./ResultsList"
import { SearchTypeToggle } from "./SearchTypeToggle"

type PropertyAutocompleteProps = {
  searchBaseUrl: string
  placeholder: string
  defaultTab: PropertyTab
  tabs?: PropertyTab[]
  placeholders?: Partial<Record<PropertyTab | "agent" | "office", string>>
  variant?: EmbedUi
  onClose: () => void
  onRedirect: (href: string) => void
  /**
   * Call before an await. The returned check goes false once the surface closes.
   * Also reserves a blank tab when `openInNewTab` is set.
   */
  prepareNewTab?: () => () => boolean
}

export function PropertyAutocomplete({
  searchBaseUrl,
  placeholder,
  defaultTab,
  tabs,
  placeholders,
  variant = "lightbox",
  onClose,
  onRedirect,
  prepareNewTab,
}: PropertyAutocompleteProps) {
  const [activeTab, setActiveTab] = useState<PropertyTab>(defaultTab)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<Category[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [expanded, setExpanded] = useState(false)
  const listboxId = useId()
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const suggestionsRequested = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const isDropdown = variant === "dropdown"
  useFocusTrap(containerRef, !isDropdown)

  // Deferred to first focus so an embedded dropdown does not cost the customer's
  // page an IndexedDB read and a saved-search request on every load.
  const loadDefaultState = useCallback(() => {
    if (suggestionsRequested.current) {
      return
    }
    suggestionsRequested.current = true
    loadSuggestions(searchBaseUrl).then(setSuggestions, () => setSuggestions([]))
  }, [searchBaseUrl])

  const flatResults = useMemo(
    () =>
      results.flatMap((category) =>
        category.Results.map((result) => ({
          category: category.CategoryName,
          result,
        }))
      ),
    [results]
  )

  const runSearch = useMemo(
    () =>
      debounce(async (value: string) => {
        if (value.trim().length < MIN_QUERY) {
          setResults([])
          setLoading(false)
          setError(null)
          return
        }
        setLoading(true)
        setError(null)
        try {
          const data = await fetchPropertyAutocomplete(searchBaseUrl, value)
          setResults(Array.isArray(data) ? data : [])
        } catch {
          setResults([])
          setError("Search unavailable — try again.")
        } finally {
          setLoading(false)
        }
      }, DEBOUNCE_MS),
    [searchBaseUrl]
  )

  useEffect(() => {
    if (!isDropdown) {
      inputRef.current?.focus()
      loadDefaultState()
    }
  }, [isDropdown, loadDefaultState])

  useEffect(() => {
    setHighlightedIndex(-1)
    runSearch(query)
  }, [query, runSearch])

  const showingSuggestions =
    query.trim().length < MIN_QUERY && suggestions.length > 0

  const redirectForResult = useCallback(
    async (categoryName: Category["CategoryName"], result: CategoryResult) => {
      const href = buildPropertyHref(categoryName, result, query, {
        overrideSearchParams: overrideSearchParamsForTab(activeTab),
      })
      // Store the relative href, matching what the search app writes, so both
      // sides normalize the same way when the row is read back.
      const stillOpen = prepareNewTab?.() ?? (() => true)
      await recordRecentSearch(searchBaseUrl, {
        query: result.DisplayName,
        categoryName,
        resultId: result.ID,
        resultDisplayName: result.DisplayName,
        link: href,
      })
      if (!stillOpen()) {
        return
      }
      onRedirect(absoluteHref(searchBaseUrl, href))
    },
    [activeTab, onRedirect, prepareNewTab, query, searchBaseUrl]
  )

  const selectResult = useCallback(
    (index: number) => {
      if (showingSuggestions) {
        const suggestion = suggestions[index]
        if (suggestion) {
          onRedirect(suggestion.href)
        }
        return
      }
      const selected = flatResults[index]
      if (selected) {
        void redirectForResult(selected.category, selected.result)
      }
    },
    [flatResults, onRedirect, redirectForResult, showingSuggestions, suggestions]
  )

  const items: ResultItem[] = showingSuggestions
    ? suggestions.map((suggestion) => ({
        key: suggestion.key,
        title: suggestion.title,
        meta: suggestion.meta,
        section: suggestion.section,
      }))
    : flatResults.map((item, index) => ({
        key: `${item.category}-${item.result.ID}-${index}`,
        title: item.result.DisplayName,
        meta: getCategoryLabel(item.category),
      }))

  const handleEnter = useCallback(() => {
    if (highlightedIndex >= 0) {
      selectResult(highlightedIndex)
      return
    }
    // With an empty box the list is the visitor's saved/recent searches, so
    // Enter must not pick one for them — only a typed query has a best match.
    if (!showingSuggestions && flatResults.length > 0) {
      selectResult(0)
    }
  }, [flatResults, highlightedIndex, selectResult, showingSuggestions])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault()
      if (isDropdown) {
        setExpanded(false)
        return
      }
      onClose()
      return
    }
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setHighlightedIndex((prev) => (prev < items.length - 1 ? prev + 1 : prev))
      return
    }
    if (event.key === "ArrowUp") {
      event.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1))
      return
    }
    if (event.key === "Enter") {
      event.preventDefault()
      handleEnter()
    }
  }

  const resolvedPlaceholder = placeholders?.[activeTab] ?? placeholder

  const showTabs = tabs && tabs.length > 1

  return (
    <AutocompleteShell
      variant={variant}
      ariaLabel="Property search"
      containerRef={containerRef}
      inputRef={inputRef}
      listboxId={listboxId}
      highlightedIndex={highlightedIndex}
      placeholder={resolvedPlaceholder}
      query={query}
      onQueryChange={(value) => {
        setQuery(value)
        setExpanded(true)
      }}
      onKeyDown={handleKeyDown}
      onFocus={() => {
        setExpanded(true)
        loadDefaultState()
      }}
      onFocusLeave={() => setExpanded(false)}
      onClose={onClose}
      toolbar={
        showTabs && tabs ? (
          <SearchTypeToggle tabs={tabs} active={activeTab} onChange={setActiveTab} />
        ) : undefined
      }
      showResults={
        !isDropdown ||
        (expanded && (query.trim().length >= MIN_QUERY || showingSuggestions))
      }
      results={
        <ResultsList
          items={items}
          loading={loading}
          error={error}
          query={query}
          highlightedIndex={highlightedIndex}
          listboxId={listboxId}
          onHighlight={setHighlightedIndex}
          onSelect={selectResult}
        />
      }
    />
  )
}
